// Admin · Accounts (Lead-only). Add & manage teacher accounts from the UI so teacher churn no longer
// needs SQL re-seeding. List (name · email · role · active) → Add teacher · Reset passcode ·
// Deactivate/Reactivate · change role. Calm + one-thumb: account state is coordination/admin, never a
// score. A passcode is a credential — it's set here but NEVER shown back. Deactivating keeps history.

import { api } from "../api";
import { esc } from "../dom";
import { me } from "../state";

interface AdminUser {
  id: string;
  name: string;
  email: string;
  auth_role: "teacher" | "lead";
  coaching_posture: "junior" | "regular" | "senior" | null;
  active: boolean;
}

export async function renderAdmin(app: HTMLElement): Promise<void> {
  const meId = me()?.id;
  let flash = ""; // a calm one-shot confirmation, re-rendered once by load()

  const rowHtml = (u: AdminUser): string => {
    const self = u.id === meId;
    const stateChip = u.active ? "" : ` <span class="chip">inactive</span>`;
    // A Lead can't deactivate or re-role their OWN account (no self-lockout) — only reset its passcode.
    const toggle = self
      ? ""
      : `<a href="#" data-act="toggle" class="muted">${u.active ? "Deactivate" : "Reactivate"}</a>`;
    const roleAct = self
      ? ""
      : `<a href="#" data-act="role" class="muted">Make ${u.auth_role === "lead" ? "teacher" : "lead"}</a>`;
    return `<div class="card user-row${u.active ? "" : " removed"}" data-id="${esc(u.id)}" data-active="${
      u.active ? 1 : 0
    }" data-role="${esc(u.auth_role)}">
        <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px">
          <strong class="uname">${esc(u.name)}</strong>
          <span><span class="chip">${esc(u.auth_role)}</span>${stateChip}</span>
        </div>
        <div class="muted" style="font-size:var(--text-sm);margin-top:2px">${esc(u.email)}${
          self ? " · you" : ""
        }</div>
        <div class="row-actions" style="margin-top:10px;gap:16px">
          <a href="#" data-act="reset" class="muted">Reset passcode</a>
          ${toggle}
          ${roleAct}
        </div>
      </div>`;
  };

  async function load(): Promise<void> {
    const { users } = await api<{ users: AdminUser[] }>("/api/admin/users");
    app.innerHTML = `
      <div class="topbar"><a data-link href="/" class="muted">← Home</a></div>
      <h1>Accounts</h1>
      <p class="hint">Add and manage teacher accounts. Deactivating keeps a teacher's history — it just stops sign-in.</p>
      ${flash ? `<div class="toast">${esc(flash)}</div>` : ""}
      <div id="err" class="error" hidden></div>
      <div id="user-list">${users.map(rowHtml).join("")}</div>

      <h2>Add a teacher</h2>
      <form id="add">
        <label for="uname">Name</label>
        <input id="uname" placeholder="Sophea" required />
        <label for="uemail">Email</label>
        <input id="uemail" type="email" placeholder="sophea@eden.gtis" required />
        <label for="upass">Initial passcode</label>
        <input id="upass" placeholder="at least 4 characters" required />
        <label for="urole">Role</label>
        <select id="urole">
          <option value="teacher" selected>Teacher</option>
          <option value="lead">Lead</option>
        </select>
        <div style="height:12px"></div>
        <button class="btn" type="submit">Add teacher</button>
      </form>`;
    flash = "";
    wire();
  }

  function wire(): void {
    const err = app.querySelector<HTMLElement>("#err")!;
    const showErr = (m: string): void => { err.textContent = m; err.hidden = false; };
    const val = (sel: string): string => (app.querySelector(sel) as HTMLInputElement | HTMLSelectElement).value.trim();

    app.querySelector<HTMLFormElement>("#add")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.hidden = true;
      const name = val("#uname");
      const email = val("#uemail");
      const passcode = val("#upass");
      const auth_role = val("#urole");
      if (!name || !email || !passcode) return;
      try {
        await api("/api/admin/users", { method: "POST", body: JSON.stringify({ name, email, passcode, auth_role }) });
        flash = `Added ${name}.`;
        await load();
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });

    // One delegated handler for reset / toggle / role / save / cancel on the user rows.
    app.querySelector<HTMLElement>("#user-list")!.addEventListener("click", async (e) => {
      const a = (e.target as HTMLElement).closest("a[data-act]") as HTMLAnchorElement | null;
      if (!a) return;
      e.preventDefault();
      const row = a.closest<HTMLElement>(".user-row")!;
      const id = row.dataset.id!;
      const act = a.dataset.act;
      const active = row.dataset.active === "1";
      const role = row.dataset.role;

      if (act === "toggle") {
        try {
          await api(`/api/admin/users/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ active: !active }) });
          flash = active ? "Account deactivated." : "Account reactivated.";
          await load();
        } catch (ex) { showErr((ex as Error).message); }
        return;
      }

      if (act === "role") {
        const next = role === "lead" ? "teacher" : "lead";
        try {
          await api(`/api/admin/users/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ auth_role: next }) });
          flash = `Role changed to ${next}.`;
          await load();
        } catch (ex) { showErr((ex as Error).message); }
        return;
      }

      if (act === "reset") {
        // Swap the action row for an inline "set a new passcode" field (the Lead tells the teacher).
        const actions = a.closest<HTMLElement>(".row-actions")!;
        actions.innerHTML = `<input class="reset-input" placeholder="New passcode" style="flex:1;min-width:130px" />
          <a href="#" data-act="save" class="muted">Save</a>
          <a href="#" data-act="cancel" class="muted">Cancel</a>`;
        actions.querySelector<HTMLInputElement>(".reset-input")!.focus();
        return;
      }

      if (act === "cancel") { await load(); return; }

      if (act === "save") {
        const pass = row.querySelector<HTMLInputElement>(".reset-input")!.value.trim();
        if (!pass) { await load(); return; }
        try {
          await api(`/api/admin/users/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ passcode: pass }) });
          flash = "Passcode updated.";
          await load();
        } catch (ex) { showErr((ex as Error).message); }
      }
    });
  }

  await load();
}
